import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Youtube} from 'lucide-react'
import EditableContent from './EditableContent'
import EditModeToggle from './EditModeToggle'
import { useAuth } from '../hooks/useAuth'

const Footer: React.FC = () => {
  const { isAdmin } = useAuth()
  const [isEditMode, setIsEditMode] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)

  // [스타일] 검은 글씨 + HTML 태그 스타일 복구 (가독성 확보)
  const htmlStyles = "text-gray-800 text-left leading-relaxed [&_p]:mb-4 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3 [&_h4]:text-lg [&_h4]:font-bold [&_h4]:mt-6 [&_h4]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_li]:mb-1 [&_strong]:font-bold"

  return (
    <footer className="bg-gray-900 text-white relative">
      {/* 편집 모드 토글 버튼 */}
      {isAdmin && (
        <div className="absolute top-4 right-4 z-10">
          <EditModeToggle 
            isEditMode={isEditMode} 
            onToggle={() => setIsEditMode(!isEditMode)}
            size="sm"
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* 회사 정보 */}
          <div className="lg:col-span-2">
            <EditableContent
              contentKey="footer_company_logo"
              defaultContent="https://cdn-static-lumi.artvibe.ai/9d/9dde493dfb780f8d3ef73ae7a7be560e.webp"
              contentType="image"
              className="h-8 mb-4"
              isEditMode={isEditMode}
            />
            <EditableContent
              contentKey="footer_company_description"
              defaultContent="X:CONNECT는 2008년 설립 이후 17년간 LG유플러스와 LG전자의 최우수 파트너로서 ICT와 가전 분야에서 검증된 전문성을 쌓아온 기업입니다. 국내 유일의 IT·가전 One-stop Service 제공 기업으로서 고객의 성장을 함께하고 있습니다."
              tag="p"
              className="text-gray-300 mb-6 leading-relaxed"
              isEditMode={isEditMode}
              multiline={true}
            />
            <div className="space-y-2 text-sm text-gray-300">
              <EditableContent contentKey="footer_company_ceo" defaultContent="대표이사: 최광수" tag="p" className="text-sm text-gray-300" isEditMode={isEditMode} />
              <EditableContent contentKey="footer_company_registration" defaultContent="사업자등록번호: 122-81-98614" tag="p" className="text-sm text-gray-300" isEditMode={isEditMode} />
              <EditableContent contentKey="footer_company_ecommerce" defaultContent="통신판매업신고번호: 제 2016-인천부평-0032호" tag="p" className="text-sm text-gray-300" isEditMode={isEditMode} />
              <EditableContent contentKey="footer_address" defaultContent="인천시 부평구 대정로 7 한라비발디 215~219호" tag="p" className="text-sm text-gray-300" isEditMode={isEditMode} />
              <EditableContent contentKey="footer_phone" defaultContent="1644-2262" tag="p" className="text-sm text-gray-300" isEditMode={isEditMode} />
              <EditableContent contentKey="footer_email" defaultContent="contact@xconnect.co.kr" tag="p" className="text-sm text-gray-300" isEditMode={isEditMode} />
            </div>
          </div>

          {/* 회사 링크 */}
          <div>
            <EditableContent contentKey="footer_company_title" defaultContent="회사" tag="h3" className="text-lg font-semibold mb-4" isEditMode={isEditMode} />
            <ul className="space-y-3">
              <li><Link to="/about" className="text-gray-300 hover:text-blue-400 transition-colors block"><EditableContent contentKey="footer_about" defaultContent="회사 소개" tag="span" className="hover:text-blue-400 transition-colors" isEditMode={isEditMode} /></Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-blue-400 transition-colors block"><EditableContent contentKey="footer_history" defaultContent="회사 연혁" tag="span" className="hover:text-blue-400 transition-colors" isEditMode={isEditMode} /></Link></li>
              <li><Link to="/portfolio" className="text-gray-300 hover:text-blue-400 transition-colors block"><EditableContent contentKey="footer_portfolio" defaultContent="포트폴리오" tag="span" className="hover:text-blue-400 transition-colors" isEditMode={isEditMode} /></Link></li>
              <li><Link to="/news" className="text-gray-300 hover:text-blue-400 transition-colors block"><EditableContent contentKey="footer_news" defaultContent="뉴스 & 블로그" tag="span" className="hover:text-blue-400 transition-colors" isEditMode={isEditMode} /></Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors block"><EditableContent contentKey="footer_contact" defaultContent="문의하기" tag="span" className="hover:text-blue-400 transition-colors" isEditMode={isEditMode} /></Link></li>
            </ul>
          </div>
        </div>

        {/* 소셜 미디어 및 저작권 */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors"><Facebook size={20} /></a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors"><Twitter size={20} /></a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors"><Linkedin size={20} /></a>
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors"><Youtube size={20} /></a>
            </div>
            
            <div className="text-center md:text-right">
              <EditableContent contentKey="footer_copyright" defaultContent="© 2025 X:CONNECT. All rights reserved." tag="p" className="text-sm text-gray-400" isEditMode={isEditMode} />
              <div className="flex flex-wrap justify-center md:justify-end space-x-6 mt-2">
                <button onClick={() => setShowPrivacyModal(true)} className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer">개인정보처리방침</button>
                <button onClick={() => setShowTermsModal(true)} className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer">이용약관</button>
                <Link to="/about" className="text-xs text-gray-400 hover:text-white transition-colors">사이트맵</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 개인정보처리방침 모달 */}
      {showPrivacyModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900">개인정보처리방침</h2>
              <button onClick={() => setShowPrivacyModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="px-8 py-8">
              {/* 엑스파워정보통신 개인정보처리방침 */}
              <EditableContent
                contentKey="privacy_policy_content"
                defaultContent={`<div class="space-y-4">
                  <p><strong>주식회사 엑스파워정보통신</strong>(이하 '회사')는 정보주체의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관계 법령이 정한 바를 준수하여, 적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다. 이에 따라 정보주체에게 개인정보 처리에 관한 절차 및 기준을 안내하고, 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 다음과 같이 개인정보 처리방침을 수립·공개합니다.</p>
                  
                  <h3>제1조 (개인정보의 처리 목적)</h3>
                  <p>회사는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
                  <ul>
                    <li><strong>고객 문의 및 상담 관리</strong><br/>서비스 이용 관련 문의, 제휴/파트너십 제안, 견적 상담, 본인 확인, 문의 사항에 대한 사실 확인, 처리 결과 통보 등을 목적으로 개인정보를 처리합니다.</li>
                  </ul>

                  <h3>제2조 (개인정보의 수집 항목)</h3>
                  <p>회사는 고객 상담 및 문의 처리를 위해 아래와 같은 개인정보를 수집하고 있습니다.</p>
                  <ul>
                    <li><strong>수집 항목 (필수)</strong>: 성명, 이메일, 회사명, 연락처(휴대전화번호 또는 유선전화번호)</li>
                    <li><strong>수집 항목 (선택)</strong>: 문의 제목, 문의 내용에 포함된 개인정보</li>
                    <li><strong>서비스 이용 과정에서 자동 생성되어 수집될 수 있는 항목</strong>: IP주소, 쿠키, 방문 일시, 서비스 이용 기록, 불량 이용 기록</li>
                  </ul>

                  <h3>제3조 (개인정보의 처리 및 보유 기간)</h3>
                  <p>① 회사는 법령에 따른 개인정보 보유·이용 기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용 기간 내에서 개인정보를 처리·보유합니다.</p>
                  <p>② 구체적인 개인정보 처리 및 보유 기간은 다음과 같습니다.</p>
                  <ul>
                    <li><strong>보유 근거</strong>: 고객 문의 및 제휴 상담 이력 관리</li>
                    <li><strong>보유 기간</strong>: 문의 접수 및 처리 완료 후 <strong>3년</strong>간 보관<br/>(단, 관계 법령 위반에 따른 수사·조사 등이 진행 중인 경우에는 해당 수사·조사 종료 시까지)</li>
                  </ul>

                  <h3>제4조 (개인정보의 파기 절차 및 방법)</h3>
                  <p>① 회사는 개인정보 보유 기간의 경과, 처리 목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체 없이 해당 개인정보를 파기합니다.</p>
                  <p>② 파기 절차 및 방법은 다음과 같습니다.</p>
                  <ul>
                    <li><strong>파기 절차</strong>: 파기 사유가 발생한 개인정보를 선정하고, 회사의 개인정보 보호책임자의 승인을 받아 개인정보를 파기합니다.</li>
                    <li><strong>파기 방법</strong>: 전자적 파일 형태로 기록·저장된 개인정보는 기록을 재생할 수 없도록 파기하며, 종이 문서에 기록·저장된 개인정보는 분쇄하거나 소각하여 파기합니다.</li>
                  </ul>

                  <h3>제5조 (개인정보의 제3자 제공)</h3>
                  <p>회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 「개인정보 보호법」 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.</p>

                  <h3>제6조 (개인정보 보호책임자)</h3>
                  <p>회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의 불만 처리 및 피해 구제 등을 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
                  <ul>
                    <li><strong>개인정보 보호책임자</strong></li>
                    <li>성명: 임병혁</li>
                    <li>직책: 상무이사</li>
                    <li>연락처: yimbh@xpowerint.com</li>
                  </ul>

                  <h3>제7조 (개인정보 처리방침의 변경)</h3>
                  <p>이 개인정보 처리방침은 <strong>2025년 12월 1일</strong>부터 적용됩니다.</p>
                </div>`}
                contentType="html"
                className={htmlStyles} 
                isEditMode={isEditMode}
              />
            </div>
          </div>
        </div>
      )}

      {/* 이용약관 모달 */}
      {showTermsModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTermsModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900">이용약관</h2>
              <button onClick={() => setShowTermsModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="px-8 py-8">
              {/* [수정 완료] 보내주신 엑스파워정보통신 서비스 이용약관 내용을 HTML로 변환하여 적용 */}
              <EditableContent
                contentKey="terms_of_service_content"
                defaultContent={`<div class="space-y-4">
                  <h3>제1조 (목적)</h3>
                  <p>본 약관은 <strong>[주식회사 엑스파워정보통신]</strong>(이하 "회사")가 제공하는 X:CONNECT 웹사이트 및 제반 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.</p>

                  <h3>제2조 (용어의 정의)</h3>
                  <p>본 약관에서 사용하는 용어의 정의는 다음과 같습니다.</p>
                  <ul>
                    <li><strong>"서비스"</strong>라 함은 단말기(PC, 휴대형 단말기 등 각종 유무선 장치를 포함)와 상관없이 회원이 이용할 수 있는 X:CONNECT 관련 제반 서비스를 의미합니다.</li>
                    <li><strong>"이용자"</strong>라 함은 서비스에 접속하여 본 약관에 따라 회사가 제공하는 서비스를 이용하는 회원 및 비회원을 말합니다.</li>
                    <li><strong>"비회원"</strong>이라 함은 회원에 가입하지 않고 회사가 제공하는 서비스를 이용하는 자를 말합니다.</li>
                  </ul>

                  <h3>제3조 (약관의 게시와 개정)</h3>
                  <p>회사는 본 약관의 내용을 이용자가 쉽게 알 수 있도록 서비스 초기 화면에 게시합니다.</p>
                  <p>회사는 「약관의 규제에 관한 법률」, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 등 관련 법령을 위배하지 않는 범위에서 이 약관을 개정할 수 있습니다.</p>
                  <p>회사가 약관을 개정할 경우에는 적용일자 및 개정 사유를 명시하여 현행 약관과 함께 서비스 초기 화면에 그 적용일자 7일 전부터 공지합니다.</p>

                  <h3>제4조 (서비스의 제공 및 변경)</h3>
                  <p>회사는 다음과 같은 업무를 수행합니다.</p>
                  <ul>
                    <li>X:CONNECT 솔루션 및 제품에 대한 정보 제공</li>
                    <li>견적 상담, 제휴 문의 등 고객 상담 서비스</li>
                    <li>기타 회사가 정하는 업무</li>
                  </ul>
                  <p>회사는 기술적 사양의 변경이나 기타 불가피한 사유가 있는 경우 제공하는 서비스의 내용을 변경할 수 있습니다.</p>

                  <h3>제5조 (서비스의 중단)</h3>
                  <p>회사는 컴퓨터 등 정보통신설비의 보수 점검, 교체 및 고장, 통신의 두절 등의 사유가 발생한 경우에는 서비스의 제공을 일시적으로 중단할 수 있습니다.</p>
                  <p>회사는 제1항의 사유로 서비스의 제공이 일시적으로 중단됨으로 인하여 이용자 또는 제3자가 입은 손해에 대하여 배상하지 않습니다. 단, 회사의 고의 또는 중과실이 입증된 경우에는 그러하지 아니합니다.</p>

                  <h3>제6조 (이용자의 의무)</h3>
                  <p>이용자는 다음 행위를 하여서는 안 됩니다.</p>
                  <ul>
                    <li>신청 또는 변경 시 허위 내용의 등록 (예: 타인의 명의나 이메일 도용)</li>
                    <li>타인의 정보 도용</li>
                    <li>회사가 게시한 정보의 변경</li>
                    <li>회사가 정한 정보 이외의 정보(컴퓨터 프로그램 등) 등의 송신 또는 게시</li>
                    <li>회사 및 기타 제3자의 저작권 등 지적재산권에 대한 침해</li>
                    <li>회사 및 기타 제3자의 명예를 손상시키거나 업무를 방해하는 행위</li>
                  </ul>

                  <h3>제7조 (저작권의 귀속 및 이용제한)</h3>
                  <p>회사가 작성한 저작물에 대한 저작권 및 기타 지적재산권은 회사에 귀속합니다.</p>
                  <p>이용자는 서비스를 이용함으로써 얻은 정보 중 회사에게 지적재산권이 귀속된 정보를 회사의 사전 승낙 없이 복제, 송신, 출판, 배포, 방송 기타 방법에 의하여 영리 목적으로 이용하거나 제3자에게 이용하게 하여서는 안 됩니다.</p>

                  <h3>제8조 (면책조항)</h3>
                  <p>회사는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.</p>
                  <p>회사는 이용자의 귀책 사유로 인한 서비스 이용의 장애에 대하여는 책임을 지지 않습니다.</p>
                  <p>회사는 이용자가 서비스를 이용하여 기대하는 수익을 상실한 것에 대하여 책임을 지지 않으며, 그 밖의 서비스를 통하여 얻은 자료로 인한 손해에 관하여 책임을 지지 않습니다.</p>

                  <h3>제9조 (분쟁해결 및 관할법원)</h3>
                  <p>회사와 이용자 간에 발생한 전자상거래 분쟁에 관한 소송은 제소 당시의 이용자의 주소에 의하고, 주소가 없는 경우에는 거소를 관할하는 지방법원의 전속관할로 합니다.</p>
                  <p>단, 이용자의 주소 또는 거소가 불명확한 경우에는 <strong>회사의 본점 소재지 관할 법원</strong>을 관할법원으로 합니다.</p>

                  <p class="mt-6"><strong>부칙</strong></p>
                  <p>이 약관은 2025년 12월 1일부터 적용됩니다.</p>
                </div>`}
                contentType="html"
                className={htmlStyles}
                isEditMode={isEditMode}
              />
            </div>
          </div>
        </div>
      )}
    </footer>
  )
}

export default Footer